/**
 * M.A.N.A — Editorial Luxe
 * Motion system (GSAP + ScrollTrigger + Lenis) and storefront behavior.
 * All animation is progressive enhancement: without JS or with reduced
 * motion, every element stays fully visible.
 */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var HAS_GSAP = typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';
  var MOTION = HAS_GSAP && !REDUCED;
  var lenis = null;

  /* If GSAP failed to load, drop the .js class so CSS initial-hidden
     states never apply and content stays visible. */
  if (!HAS_GSAP) {
    document.documentElement.classList.remove('js');
  }

  /* ===========================================
     Utilities
     =========================================== */
  function formatMoney(cents) {
    var format = (window.ManaShop && window.ManaShop.moneyFormat) || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var withComma = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount': return withComma;
        case 'amount_no_decimals': return Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        case 'amount_with_comma_separator': return value.replace('.', ',');
        default: return withComma;
      }
    });
  }

  function toCents(v) {
    if (typeof v === 'string' && v.indexOf('.') !== -1) return Math.round(parseFloat(v) * 100);
    return Number(v);
  }

  function rootUrl() {
    var root = (window.ManaShop && window.ManaShop.rootUrl) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  /* ===========================================
     1. Lenis — smooth inertia scroll
     =========================================== */
  function initLenis() {
    if (!MOTION || typeof window.Lenis === 'undefined') return;

    lenis = new Lenis({
      duration: 1.15,
      smoothWheel: true
    });

    lenis.on('scroll', ScrollTrigger.update);

    gsap.ticker.add(function (time) {
      lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);
  }

  /* ===========================================
     2. Reveals — [data-reveal], headline line masks
     Hero elements animate immediately on load (no scroll
     dependency — on short screens the CTAs can sit below the
     trigger line and would never appear). Everything else
     reveals on scroll.
     =========================================== */
  function initHeroIntro() {
    if (!MOTION) return;
    var hero = document.querySelector('.mana-hero');
    if (!hero) return;

    var inners = hero.querySelectorAll('.mana-line__inner');
    if (inners.length) {
      gsap.fromTo(inners, { yPercent: 115 }, {
        yPercent: 0,
        duration: 1.25,
        stagger: 0.12,
        delay: 0.15,
        ease: 'power4.out'
      });
    }

    hero.querySelectorAll('[data-reveal]').forEach(function (el) {
      var type = el.getAttribute('data-reveal') || 'up';
      var delay = parseFloat(el.getAttribute('data-reveal-delay') || '0');
      var from = { opacity: 0 };
      if (type === 'up') from.y = 32;
      if (type === 'scale') from.scale = 0.96;

      gsap.fromTo(el, from, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 1.1,
        delay: delay,
        ease: 'power3.out'
      });
    });
  }

  function initReveals() {
    if (!MOTION) return;

    gsap.utils.toArray('[data-reveal]').forEach(function (el) {
      if (el.closest('.mana-hero')) return; /* handled by initHeroIntro */
      var type = el.getAttribute('data-reveal') || 'up';
      var delay = parseFloat(el.getAttribute('data-reveal-delay') || '0');
      var from = { opacity: 0 };
      if (type === 'up') from.y = 32;
      if (type === 'scale') from.scale = 0.96;

      gsap.fromTo(el, from, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 1.1,
        delay: delay,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 88%',
          once: true
        }
      });
    });

    gsap.utils.toArray('[data-lines]').forEach(function (block) {
      if (block.closest('.mana-hero')) return; /* handled by initHeroIntro */
      var inners = block.querySelectorAll('.mana-line__inner');
      if (!inners.length) return;

      gsap.fromTo(inners, { yPercent: 115 }, {
        yPercent: 0,
        duration: 1.25,
        stagger: 0.12,
        ease: 'power4.out',
        scrollTrigger: {
          trigger: block,
          start: 'top 90%',
          once: true
        }
      });
    });
  }

  /* ===========================================
     3. Parallax — [data-parallax] inner wrappers
     =========================================== */
  function initParallax() {
    if (!MOTION) return;

    gsap.utils.toArray('[data-parallax]').forEach(function (el) {
      var amount = parseFloat(el.getAttribute('data-parallax') || '8');
      gsap.fromTo(el, { yPercent: -amount }, {
        yPercent: amount,
        ease: 'none',
        scrollTrigger: {
          trigger: el.parentElement,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        }
      });
    });
  }

  /* ===========================================
     4. Marquee — infinite loop, scroll-velocity boost
     =========================================== */
  function initMarquee() {
    var tracks = document.querySelectorAll('.mana-marquee__track');
    if (!tracks.length || !MOTION) return;

    tracks.forEach(function (track) {
      var speed = parseFloat(track.getAttribute('data-speed') || '26');
      var tween = gsap.to(track, {
        xPercent: -50,
        repeat: -1,
        duration: speed,
        ease: 'none'
      });

      ScrollTrigger.create({
        trigger: track,
        start: 'top bottom',
        end: 'bottom top',
        onUpdate: function (self) {
          var boost = 1 + Math.min(Math.abs(self.getVelocity()) / 900, 3);
          gsap.to(tween, { timeScale: boost, duration: 0.4, overwrite: true });
          gsap.to(tween, { timeScale: 1, duration: 1.2, delay: 0.4, overwrite: false });
        }
      });
    });
  }

  /* ===========================================
     5. Editorial — pinned media while steps scroll
     =========================================== */
  function initEditorialPin() {
    if (!MOTION) return;
    var section = document.querySelector('.mana-editorial__layout');
    if (!section) return;

    var media = section.querySelector('.mana-editorial__media');
    var steps = section.querySelector('.mana-editorial__steps');
    if (!media || !steps) return;

    var mm = gsap.matchMedia();
    mm.add('(min-width: 1024px)', function () {
      var pin = ScrollTrigger.create({
        trigger: section,
        start: 'top top+=' + 96,
        end: function () { return '+=' + Math.max(steps.offsetHeight - media.offsetHeight, 0); },
        pin: media,
        pinSpacing: false
      });
      return function () { pin.kill(); };
    });
  }

  /* ===========================================
     6. Header — frost on scroll, hide down / show up
     =========================================== */
  function initHeader() {
    var header = document.getElementById('mana-header');
    if (!header) return;

    var lastY = 0;

    function onScroll(y) {
      header.classList.toggle('scrolled', y > 40);
      if (!REDUCED) {
        if (y > lastY && y > 240) {
          header.classList.add('is-hidden');
        } else {
          header.classList.remove('is-hidden');
        }
      }
      lastY = y;
    }

    if (lenis) {
      lenis.on('scroll', function (e) { onScroll(e.scroll); });
    } else {
      window.addEventListener('scroll', function () { onScroll(window.scrollY); }, { passive: true });
    }
    onScroll(window.scrollY);
  }

  /* ===========================================
     7. Scroll progress bar
     =========================================== */
  function initProgress() {
    var bar = document.getElementById('mana-progress');
    if (!bar || REDUCED) return;

    function update() {
      var max = document.documentElement.scrollHeight - window.innerHeight;
      var p = max > 0 ? window.scrollY / max : 0;
      bar.style.transform = 'scaleX(' + Math.min(Math.max(p, 0), 1) + ')';
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ===========================================
     8. Page transitions — ink curtain on navigation
     =========================================== */
  function initTransitions() {
    var curtain = document.getElementById('mana-curtain');
    if (!curtain || !MOTION) return;

    /* Restore state when returning via back/forward cache */
    window.addEventListener('pageshow', function () {
      gsap.set(curtain, { scaleY: 0, transformOrigin: 'bottom' });
    });

    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (!link) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (link.target === '_blank' || link.hasAttribute('download')) return;
      if (link.hasAttribute('data-no-transition')) return;

      var href = link.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;

      var url;
      try { url = new URL(link.href, window.location.href); } catch (err) { return; }
      if (url.origin !== window.location.origin) return;
      if (url.pathname.indexOf('/checkout') === 0) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;

      e.preventDefault();
      var mark = curtain.querySelector('.mana-curtain__mark');
      var tl = gsap.timeline({
        onComplete: function () { window.location.href = link.href; }
      });
      tl.set(curtain, { transformOrigin: 'bottom' })
        .to(curtain, { scaleY: 1, duration: 0.5, ease: 'power3.inOut' });
      if (mark) tl.to(mark, { opacity: 1, duration: 0.25 }, '-=0.2');
    });
  }

  /* ===========================================
     9. Magnetic buttons — [data-magnetic]
     =========================================== */
  function initMagnetic() {
    if (!MOTION || window.matchMedia('(hover: none)').matches) return;

    document.querySelectorAll('[data-magnetic]').forEach(function (el) {
      el.addEventListener('mousemove', function (e) {
        var rect = el.getBoundingClientRect();
        var x = (e.clientX - rect.left - rect.width / 2) / (rect.width / 2);
        var y = (e.clientY - rect.top - rect.height / 2) / (rect.height / 2);
        gsap.to(el, { x: x * 6, y: y * 6, duration: 0.4, ease: 'power2.out' });
      });
      el.addEventListener('mouseleave', function () {
        gsap.to(el, { x: 0, y: 0, duration: 0.6, ease: 'elastic.out(1, 0.4)' });
      });
    });
  }

  /* ===========================================
     10. Mobile menu
     =========================================== */
  function initMobileMenu() {
    var menu = document.getElementById('mana-mobile-menu');
    var openBtn = document.getElementById('mana-menu-open');
    var closeBtn = document.getElementById('mana-menu-close');
    if (!menu || !openBtn || !closeBtn) return;

    function open() {
      menu.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (lenis) lenis.stop();
    }

    function close() {
      menu.classList.remove('is-open');
      document.body.style.overflow = '';
      if (lenis) lenis.start();
    }

    openBtn.addEventListener('click', open);
    closeBtn.addEventListener('click', close);
    menu.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', close);
    });
  }

  /* ===========================================
     11. Cart count badge
     =========================================== */
  function setCartCount(n) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = n > 0 ? n : '';
      el.setAttribute('data-count', n);
    });
  }

  function refreshCartCount() {
    fetch(rootUrl() + 'cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) { setCartCount(cart.item_count); })
      .catch(function () {});
  }

  function initCartCount() {
    if (window.ManaShop && typeof window.ManaShop.cartCount === 'number') {
      setCartCount(window.ManaShop.cartCount);
    }
  }

  /* ===========================================
     12. Rails — drag to scroll + arrows
     =========================================== */
  function initRails() {
    document.querySelectorAll('.mana-rail').forEach(setupRail);
  }

  function setupRail(rail) {
    var track = rail.querySelector('.mana-rail__track');
    if (!track || track.hasAttribute('data-rail-ready')) return;
    track.setAttribute('data-rail-ready', '');

    /* Drag to scroll (desktop pointers) */
    var isDown = false;
    var startX = 0;
    var startScroll = 0;
    var moved = false;

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;
      isDown = true;
      moved = false;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      track.classList.add('is-dragging');
    });

    window.addEventListener('pointermove', function (e) {
      if (!isDown) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 6) moved = true;
      track.scrollLeft = startScroll - dx;
    });

    window.addEventListener('pointerup', function () {
      if (!isDown) return;
      isDown = false;
      track.classList.remove('is-dragging');
    });

    /* Swallow the click that ends a drag so cards don't navigate */
    track.addEventListener('click', function (e) {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    }, true);

    /* Arrows */
    var prev = rail.querySelector('[data-rail-prev]');
    var next = rail.querySelector('[data-rail-next]');

    function step() {
      var card = track.querySelector(':scope > *');
      return card ? card.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
    }

    function updateArrows() {
      if (!prev || !next) return;
      prev.disabled = track.scrollLeft <= 4;
      next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
    }

    if (prev) prev.addEventListener('click', function () {
      track.scrollBy({ left: -step() * 2, behavior: 'smooth' });
    });
    if (next) next.addEventListener('click', function () {
      track.scrollBy({ left: step() * 2, behavior: 'smooth' });
    });

    track.addEventListener('scroll', updateArrows, { passive: true });
    updateArrows();
  }

  /* ===========================================
     13. Product card renderer (client-side rails)
     =========================================== */
  function productCardHTML(p) {
    var img1 = p.featured_image || (p.images && p.images[0]) || '';
    var img2 = (p.images && p.images.length > 1) ? p.images[1] : null;
    var price = toCents(p.price);
    var compare = p.compare_at_price ? toCents(p.compare_at_price) : 0;
    var onSale = compare > price;

    var html = '<a href="' + p.url + '" class="mana-product-card">';
    html += '<div class="mana-product-card__media">';
    if (onSale) html += '<span class="mana-product-card__badge mana-product-card__badge--terra">Sale</span>';
    if (img1) html += '<img class="mana-product-card__img" src="' + sizedImage(img1, 480) + '" alt="' + escapeHtml(p.title) + '" loading="lazy">';
    if (img2) html += '<img class="mana-product-card__img mana-product-card__img--hover" src="' + sizedImage(img2, 480) + '" alt="" loading="lazy" aria-hidden="true">';
    html += '</div>';
    html += '<div class="mana-product-card__body">';
    html += '<h3 class="mana-product-card__title">' + escapeHtml(p.title) + '</h3>';
    html += '<div class="mana-product-card__pricing">';
    html += '<span class="mana-product-card__price">' + formatMoney(price) + '</span>';
    if (onSale) html += '<span class="mana-product-card__compare">' + formatMoney(compare) + '</span>';
    html += '</div></div></a>';
    return html;
  }

  function sizedImage(src, width) {
    if (!src) return src;
    if (src.indexOf('cdn.shopify.com') === -1 && src.indexOf('/cdn/shop/') === -1) return src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  /* ===========================================
     14. Recently viewed — capture + render
     =========================================== */
  var RV_KEY = 'mana_recently_viewed';

  function readRecentlyViewed() {
    try {
      return JSON.parse(localStorage.getItem(RV_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function captureRecentlyViewed() {
    var metaEl = document.getElementById('mana-product-meta');
    if (!metaEl) return;

    var meta;
    try { meta = JSON.parse(metaEl.textContent); } catch (e) { return; }
    if (!meta || !meta.id) return;

    var list = readRecentlyViewed().filter(function (item) {
      return item.id !== meta.id;
    });
    list.unshift(meta);
    list = list.slice(0, 12);

    try {
      localStorage.setItem(RV_KEY, JSON.stringify(list));
      localStorage.setItem('mana_last_viewed_id', String(meta.id));
    } catch (e) { /* storage full / private mode */ }
  }

  function initRecentlyViewedSection() {
    var section = document.querySelector('[data-recently-viewed]');
    if (!section) return;

    var track = section.querySelector('.mana-rail__track');
    var limit = parseInt(section.getAttribute('data-limit') || '8', 10);
    var exclude = section.getAttribute('data-exclude');

    var items = readRecentlyViewed().filter(function (item) {
      return String(item.id) !== String(exclude);
    }).slice(0, limit);

    if (!items.length || !track) return;

    var html = '';
    items.forEach(function (item) {
      html += '<a href="' + item.url + '" class="mana-product-card">' +
        '<div class="mana-product-card__media">' +
        (item.image ? '<img class="mana-product-card__img" src="' + sizedImage(item.image, 400) + '" alt="' + escapeHtml(item.title) + '" loading="lazy">' : '') +
        '</div>' +
        '<div class="mana-product-card__body">' +
        '<h3 class="mana-product-card__title">' + escapeHtml(item.title) + '</h3>' +
        '<div class="mana-product-card__pricing"><span class="mana-product-card__price">' + escapeHtml(item.price) + '</span></div>' +
        '</div></a>';
    });

    track.innerHTML = html;
    section.removeAttribute('data-pending');
    setupRail(section.querySelector('.mana-rail') || section);
    refreshSectionMotion(section);
  }

  /* ===========================================
     15. Recommendations — Shopify API rails
     =========================================== */
  function initRecommendations() {
    document.querySelectorAll('[data-recommendations]').forEach(function (section) {
      var track = section.querySelector('[data-recs-track]');
      var seed = section.getAttribute('data-seed');
      var intent = section.getAttribute('data-intent') || 'related';
      var limit = section.getAttribute('data-limit') || '8';
      var hasServer = section.getAttribute('data-has-server') === 'true';

      if (!seed) {
        seed = localStorage.getItem('mana_last_viewed_id');
      }

      if (!seed || !track) {
        if (hasServer) section.removeAttribute('data-pending');
        return;
      }

      var url = rootUrl() + 'recommendations/products.json?product_id=' + encodeURIComponent(seed) +
        '&limit=' + encodeURIComponent(limit) + '&intent=' + encodeURIComponent(intent);

      fetch(url)
        .then(function (res) {
          if (!res.ok) throw new Error('recs ' + res.status);
          return res.json();
        })
        .then(function (data) {
          var products = (data && data.products) || [];
          var exclude = section.getAttribute('data-exclude');
          if (exclude) {
            products = products.filter(function (p) { return String(p.id) !== String(exclude); });
          }

          if (!products.length) {
            if (intent === 'complementary' && !section.hasAttribute('data-no-fallback')) {
              /* Fall back to related products once */
              return retryAsRelated(section, track, seed, limit, hasServer);
            }
            if (hasServer) section.removeAttribute('data-pending');
            return;
          }

          renderRecs(section, track, products);
        })
        .catch(function () {
          if (hasServer) section.removeAttribute('data-pending');
        });
    });
  }

  function retryAsRelated(section, track, seed, limit, hasServer) {
    var url = rootUrl() + 'recommendations/products.json?product_id=' + encodeURIComponent(seed) +
      '&limit=' + encodeURIComponent(limit) + '&intent=related';
    fetch(url)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var products = (data && data.products) || [];
        var exclude = section.getAttribute('data-exclude');
        if (exclude) {
          products = products.filter(function (p) { return String(p.id) !== String(exclude); });
        }
        if (products.length) {
          renderRecs(section, track, products);
        } else if (hasServer) {
          section.removeAttribute('data-pending');
        }
      })
      .catch(function () {
        if (hasServer) section.removeAttribute('data-pending');
      });
  }

  function renderRecs(section, track, products) {
    var html = '';
    products.forEach(function (p) { html += productCardHTML(p); });
    track.innerHTML = html;
    section.removeAttribute('data-pending');
    var rail = track.closest('.mana-rail');
    if (rail) setupRail(rail);
    refreshSectionMotion(section);
  }

  function refreshSectionMotion(section) {
    if (!MOTION) return;
    ScrollTrigger.refresh();
    var cards = section.querySelectorAll('.mana-product-card');
    gsap.fromTo(cards, { opacity: 0, y: 24 }, {
      opacity: 1,
      y: 0,
      duration: 0.9,
      stagger: 0.07,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: section,
        start: 'top 90%',
        once: true
      }
    });
  }

  /* ===========================================
     16. Product page — variants, gallery, add to cart
     =========================================== */
  function initProductPage() {
    var pdp = document.querySelector('.mana-pdp');
    if (!pdp) return;

    var variantJsonEl = document.getElementById('mana-pdp-variant-json');
    if (!variantJsonEl) return;

    var data = JSON.parse(variantJsonEl.textContent);
    var variants = data.variants;
    var allImages = data.all_images;
    var colorIndex = data.option_names.findIndex(function (name) { return /^(color|colour)$/i.test(name); });
    var colors = colorIndex < 0 ? [] : Array.from(new Set(variants.map(function (v) { return v.options[colorIndex]; })));
    var galleryKey = '';
    var dotObserver;

    var form = document.getElementById('mana-pdp-form');
    var variantInput = document.getElementById('mana-pdp-variant-id');
    var addBtn = document.getElementById('mana-pdp-add-btn');
    var gallery = document.getElementById('mana-pdp-gallery');
    var dotsWrap = document.getElementById('mana-pdp-gallery-dots');

    function normalize(value) {
      return ' ' + String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim() + ' ';
    }

    function imageColors(image) {
      var assigned = variants.filter(function (v) {
        return (image.variant_ids || []).indexOf(v.id) !== -1 || v.featured_image_id === image.id;
      }).map(function (v) { return v.options[colorIndex]; });
      if (assigned.length) return assigned;
      if (image.id === 42711265575102) return ['Pebble'];
      var filename = normalize(image.src.split('/').pop().split('?')[0]);
      var matches = colors.filter(function (color) {
        return filename.indexOf(normalize(color)) !== -1 ||
          (color === 'Olive Green' && colors.indexOf('Olive') === -1 && filename.indexOf(' olive ') !== -1);
      });
      if (matches.length === 1) return matches;
      var alt = normalize(image.alt);
      matches = colors.filter(function (color) { return alt.indexOf(normalize(color)) !== -1; });
      return matches.length === 1 ? matches : [];
    }

    var groupedImages = new Map();
    colors.forEach(function (color) {
      groupedImages.set(color, allImages.filter(function (image) { return imageColors(image).indexOf(color) !== -1; }));
    });

    function getVariantById(id) {
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === id) return variants[i];
      }
      return null;
    }

    function getImagesForVariant(variant) {
      var matchingVariants = colorIndex < 0 ? [variant] : variants.filter(function (v) {
        return v.options[colorIndex] === variant.options[colorIndex];
      });
      var curated = variant.gallery.length ? variant.gallery : (matchingVariants.find(function (v) { return v.gallery.length; }) || {}).gallery;
      if (curated && curated.length) return curated.map(function (src) {
        return allImages.find(function (image) { return image.src === src; }) || { src: src, alt: data.product_title };
      });
      var images = colorIndex < 0 ? allImages.slice() : (groupedImages.get(variant.options[colorIndex]) || []).slice();
      var featuredIndex = images.findIndex(function (image) { return image.id === variant.featured_image_id; });
      if (featuredIndex > 0) images.unshift(images.splice(featuredIndex, 1)[0]);
      if (!images.length && variant.featured_image) images.push({src: variant.featured_image, alt: data.product_title});
      return images;
    }

    function updateGallery(variant) {
      if (!gallery) return;
      var images = getImagesForVariant(variant);
      var key = images.map(function (image) { return image.src; }).join('|');
      if (key === galleryKey) return;
      galleryKey = key;
      gallery.querySelectorAll('.mana-pdp__gallery-item').forEach(function (el) { el.remove(); });
      images.forEach(function (image, idx) {
        var div = document.createElement('div');
        div.className = 'mana-pdp__gallery-item';
        var img = document.createElement('img');
        img.src = image.src;
        img.alt = image.alt || data.product_title + (colorIndex < 0 ? '' : ' — ' + variant.options[colorIndex]);
        img.className = 'mana-pdp__gallery-img';
        img.loading = idx === 0 ? 'eager' : 'lazy';
        if (image.width && image.height) { img.width = image.width; img.height = image.height; }
        div.appendChild(img);
        gallery.appendChild(div);
      });
      updateDots();
      gallery.scrollTo({ left: 0, behavior: 'instant' });
      if (MOTION) ScrollTrigger.refresh();
    }

    function updatePrice(variant) {
      var priceEl = pdp.querySelector('[data-price]');
      var compareEl = pdp.querySelector('[data-compare-price]');
      var btnPrice = pdp.querySelector('[data-add-price]');
      var barPrice = document.querySelector('[data-bar-price]');

      if (priceEl) priceEl.textContent = variant.price_formatted;
      if (btnPrice) btnPrice.textContent = variant.price_formatted;
      if (barPrice) barPrice.textContent = variant.price_formatted;
      if (compareEl) {
        if (variant.compare_at_price > variant.price) {
          compareEl.textContent = variant.compare_at_price_formatted;
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
    }

    function updateButton(variant) {
      var btnText = addBtn.querySelector('[data-add-text]');
      var barBtn = document.getElementById('mana-pdp-bar-btn');
      if (variant.available) {
        addBtn.disabled = false;
        if (btnText) btnText.textContent = 'Add to Bag';
        if (barBtn) { barBtn.disabled = false; barBtn.textContent = 'Add to Bag'; }
      } else {
        addBtn.disabled = true;
        if (btnText) btnText.textContent = 'Sold Out';
        if (barBtn) { barBtn.disabled = true; barBtn.textContent = 'Sold Out'; }
      }
      addBtn.classList.remove('is-added');
    }

    /* Mobile sticky add-to-bag bar — shows once the main button scrolls away */
    (function initPdpBar() {
      var bar = document.getElementById('mana-pdp-bar');
      var barBtn = document.getElementById('mana-pdp-bar-btn');
      if (!bar || !addBtn) return;

      barBtn.addEventListener('click', function () {
        if (form && !addBtn.disabled) {
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          } else {
            form.dispatchEvent(new Event('submit', { cancelable: true }));
          }
          barBtn.textContent = 'Added ✓';
          setTimeout(function () {
            if (!barBtn.disabled) barBtn.textContent = 'Add to Bag';
          }, 2000);
        }
      });

      var barObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var below = entry.boundingClientRect.top < 0;
          var show = !entry.isIntersecting && below;
          bar.classList.toggle('is-visible', show);
          bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        });
      }, { threshold: 0 });

      barObserver.observe(addBtn);
    })();

    var colorSwatches = pdp.querySelectorAll('.mana-pdp__color-swatch');
    var sizeGroups = pdp.querySelectorAll('.mana-pdp__size-options');

    function selectVariant(variant, updateUrl) {
      if (!variant) return;
      variantInput.value = variant.id;
      colorSwatches.forEach(function (swatch) {
        var active = swatch.dataset.color === variant.options[colorIndex];
        swatch.classList.toggle('is-active', active);
        swatch.setAttribute('aria-pressed', String(active));
      });
      var colorLabel = pdp.querySelector('[data-color-label]');
      if (colorLabel) colorLabel.textContent = variant.options[colorIndex];
      sizeGroups.forEach(function (group) {
        group.querySelectorAll('.mana-pdp__size-btn').forEach(function (btn) {
          var active = btn.dataset.value === variant.options[Number(group.dataset.optionIndex)];
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-pressed', String(active));
        });
      });
      updateGallery(variant);
      updatePrice(variant);
      updateButton(variant);
      if (updateUrl) {
        var url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    function changeOption(index, value) {
      var current = getVariantById(Number(variantInput.value));
      if (!current) return;
      var options = current.options.slice();
      options[index] = value;
      var next = variants.find(function (v) { return v.options.every(function (option, i) { return option === options[i]; }); });
      if (!next) next = variants.find(function (v) { return v.options[index] === value && v.available; }) ||
        variants.find(function (v) { return v.options[index] === value; });
      selectVariant(next, true);
    }

    colorSwatches.forEach(function (swatch) {
      swatch.addEventListener('click', function () { changeOption(colorIndex, swatch.dataset.color); });
      var images = groupedImages.get(swatch.dataset.color) || [];
      if (!swatch.querySelector('img') && images.length) {
        swatch.textContent = '';
        var image = document.createElement('img');
        image.src = images[0].src.replace('width=1200', 'width=100');
        image.alt = swatch.dataset.color;
        image.className = 'mana-pdp__swatch-img';
        image.width = 100;
        image.height = 100;
        swatch.appendChild(image);
      }
    });
    sizeGroups.forEach(function (group) {
      group.querySelectorAll('.mana-pdp__size-btn').forEach(function (btn) {
        btn.addEventListener('click', function () { changeOption(Number(group.dataset.optionIndex), btn.dataset.value); });
      });
    });

    /* Accordions */
    pdp.querySelectorAll('.mana-pdp__accordion-trigger').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var accordion = trigger.closest('.mana-pdp__accordion');
        var isOpen = accordion.classList.contains('is-open');
        pdp.querySelectorAll('.mana-pdp__accordion').forEach(function (a) {
          a.classList.remove('is-open');
          a.querySelector('.mana-pdp__accordion-trigger').setAttribute('aria-expanded', 'false');
        });
        if (!isOpen) {
          accordion.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });

    /* Mobile gallery dots */
    function updateDots() {
      if (!dotsWrap) return;
      if (dotObserver) dotObserver.disconnect();
      dotsWrap.innerHTML = '';
      var items = gallery.querySelectorAll('.mana-pdp__gallery-item');
      if (items.length <= 1) return;

      items.forEach(function (item, idx) {
        var dot = document.createElement('button');
        dot.className = 'mana-pdp__gallery-dot' + (idx === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Image ' + (idx + 1));
        dot.addEventListener('click', function () {
          gallery.scrollTo({ left: item.offsetLeft - gallery.offsetLeft, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        });
        dotsWrap.appendChild(dot);
      });

      dotObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var allItems = gallery.querySelectorAll('.mana-pdp__gallery-item');
            var dots = dotsWrap.querySelectorAll('.mana-pdp__gallery-dot');
            allItems.forEach(function (el, i) {
              if (el === entry.target && dots[i]) {
                dots.forEach(function (d) { d.classList.remove('is-active'); });
                dots[i].classList.add('is-active');
              }
            });
          }
        });
      }, { root: gallery, threshold: 0.6 });

      items.forEach(function (item) { dotObserver.observe(item); });
    }

    selectVariant(getVariantById(Number(variantInput.value)), false);

    /* AJAX add to cart */
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (addBtn.disabled) return;

        addBtn.classList.add('is-adding');
        var btnText = addBtn.querySelector('[data-add-text]');
        var origText = btnText ? btnText.textContent : '';
        if (btnText) btnText.textContent = 'Adding...';

        fetch(rootUrl() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: parseInt(variantInput.value),
            quantity: 1
          })
        })
          .then(function (res) { return res.json(); })
          .then(function () {
            addBtn.classList.remove('is-adding');
            addBtn.classList.add('is-added');
            if (btnText) btnText.textContent = 'Added ✓';
            refreshCartCount();
            setTimeout(function () {
              addBtn.classList.remove('is-added');
              if (btnText) btnText.textContent = origText;
            }, 2000);
          })
          .catch(function () {
            addBtn.classList.remove('is-adding');
            if (btnText) btnText.textContent = origText;
          });
      });
    }
  }

  /* ===========================================
     17. Collection page — filter / sort
     =========================================== */
  function initCollectionPage() {
    var filterToggle = document.getElementById('mana-filter-toggle');
    var filterPanel = document.getElementById('mana-filter-panel');
    var filterClose = document.getElementById('mana-filter-close');
    var filterOverlay = document.getElementById('mana-filter-overlay');
    var filterApply = document.getElementById('mana-filter-apply');

    if (!filterToggle || !filterPanel) return;

    var selectedSort = null;

    function openPanel() {
      filterPanel.classList.add('is-open');
      filterOverlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (lenis) lenis.stop();
    }

    function closePanel() {
      filterPanel.classList.remove('is-open');
      filterOverlay.classList.remove('is-open');
      document.body.style.overflow = '';
      if (lenis) lenis.start();
    }

    filterToggle.addEventListener('click', openPanel);
    if (filterClose) filterClose.addEventListener('click', closePanel);
    if (filterOverlay) filterOverlay.addEventListener('click', closePanel);

    var sortBtns = document.querySelectorAll('.mana-filter-panel__sort-btn');
    sortBtns.forEach(function (btn) {
      if (btn.classList.contains('is-active')) selectedSort = btn.dataset.sort;
      btn.addEventListener('click', function () {
        sortBtns.forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
        selectedSort = btn.dataset.sort;
      });
    });

    var colorChips = document.querySelectorAll('.mana-filter-panel__color-chip');
    var allCards = document.querySelectorAll('.mana-collection__card');

    colorChips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        chip.classList.toggle('is-active');
      });
    });

    if (filterApply) {
      filterApply.addEventListener('click', function () {
        var activeChips = document.querySelectorAll('.mana-filter-panel__color-chip.is-active');
        var selectedColors = [];
        activeChips.forEach(function (chip) {
          var colorName = chip.querySelector('.mana-filter-panel__color-name');
          if (colorName) selectedColors.push(colorName.textContent.trim().toLowerCase());
        });

        var currentSort = new URL(window.location).searchParams.get('sort_by');
        if (selectedSort && selectedSort !== currentSort) {
          var url = new URL(window.location.origin + window.location.pathname);
          url.searchParams.set('sort_by', selectedSort);
          window.location.href = url.toString();
          return;
        }

        var visibleCount = 0;
        allCards.forEach(function (card) {
          if (selectedColors.length === 0) {
            card.style.display = '';
            visibleCount++;
          } else {
            var cardColors = (card.dataset.colors || '').toLowerCase().split('|');
            var match = selectedColors.some(function (sc) {
              return cardColors.some(function (cc) { return cc.trim() === sc; });
            });
            card.style.display = match ? '' : 'none';
            if (match) visibleCount++;
          }
        });

        filterApply.textContent = 'Showing ' + visibleCount + ' Products';
        closePanel();
        if (MOTION) ScrollTrigger.refresh();
      });
    }
  }

  /* ===========================================
     18. Cart page — qty & remove
     =========================================== */
  function initCartPage() {
    var cartItems = document.querySelectorAll('.mana-cart__item');
    if (!cartItems.length) return;

    function updateCart(line, quantity) {
      fetch(rootUrl() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      }).then(function () {
        window.location.reload();
      });
    }

    document.querySelectorAll('.mana-cart__qty-minus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var line = parseInt(btn.dataset.line);
        var val = parseInt(btn.parentElement.querySelector('.mana-cart__qty-value').textContent);
        if (val > 1) updateCart(line, val - 1);
      });
    });

    document.querySelectorAll('.mana-cart__qty-plus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var line = parseInt(btn.dataset.line);
        var val = parseInt(btn.parentElement.querySelector('.mana-cart__qty-value').textContent);
        updateCart(line, val + 1);
      });
    });

    document.querySelectorAll('.mana-cart__item-remove').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var line = parseInt(btn.dataset.line);
        updateCart(line, 0);
      });
    });
  }

  /* ===========================================
     19. Newsletter — Firebase
     =========================================== */
  function initNewsletter() {
    var form = document.getElementById('mana-newsletter-form');
    if (!form) return;

    var emailInput = document.getElementById('mana-newsletter-email');

    if (localStorage.getItem('mana_subscribed')) {
      form.innerHTML = '<p class="mana-footer__newsletter-note is-success">You\'re subscribed! Use code <strong>WELCOME10</strong> for 10% off.</p>';
      return;
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = emailInput.value.trim();
      if (!email) return;

      var btn = form.querySelector('.mana-footer__newsletter-btn');
      if (btn) btn.disabled = true;

      var firebaseConfig = {
        apiKey: "AIzaSyAC5S72IDVH4y3DogpCvEYKDh0hUzKznv0",
        authDomain: "mana-66ae0.firebaseapp.com",
        projectId: "mana-66ae0",
        storageBucket: "mana-66ae0.firebasestorage.app",
        messagingSenderId: "38475338187",
        appId: "1:38475338187:web:d15d32df4484e34e89a64e"
      };

      function success() {
        localStorage.setItem('mana_subscribed', email);
        form.innerHTML = '<p class="mana-footer__newsletter-note is-success">Thanks! Use code <strong>WELCOME10</strong> at checkout for 10% off your first order.</p>';
      }

      try {
        if (typeof firebase !== 'undefined') {
          if (!firebase.apps.length) firebase.initializeApp(firebaseConfig);
          firebase.firestore().collection('newsletter_subscribers').add({
            email: email,
            subscribedAt: firebase.firestore.FieldValue.serverTimestamp(),
            discountCode: 'WELCOME10',
            source: 'footer'
          }).then(success).catch(success);
        } else {
          success();
        }
      } catch (err) {
        success();
      }
    });
  }

  /* ===========================================
     20. Contact form — Firebase
     =========================================== */
  function initContactForm() {
    var form = document.getElementById('mana-contact-form');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = document.getElementById('mana-contact-submit');
      var name = document.getElementById('contact-name').value.trim();
      var email = document.getElementById('contact-email').value.trim();
      var subject = document.getElementById('contact-subject').value;
      var message = document.getElementById('contact-message').value.trim();
      if (!name || !email || !message) return;

      btn.disabled = true;
      btn.textContent = 'Sending…';

      var firebaseConfig = {
        apiKey: "AIzaSyAC5S72IDVH4y3DogpCvEYKDh0hUzKznv0",
        authDomain: "mana-66ae0.firebaseapp.com",
        projectId: "mana-66ae0",
        storageBucket: "mana-66ae0.firebasestorage.app",
        messagingSenderId: "38475338187",
        appId: "1:38475338187:web:d15d32df4484e34e89a64e"
      };

      try {
        if (typeof firebase !== 'undefined') {
          if (!firebase.apps.length) firebase.initializeApp(firebaseConfig);
          firebase.firestore().collection('contact_messages').add({
            name: name, email: email, subject: subject, message: message,
            createdAt: firebase.firestore.FieldValue.serverTimestamp()
          }).then(function () {
            btn.textContent = 'Message Sent!';
            form.reset();
            setTimeout(function () { btn.textContent = 'Send Message'; btn.disabled = false; }, 3000);
          }).catch(function () {
            btn.textContent = 'Send Message'; btn.disabled = false;
            alert('Something went wrong. Please email us directly.');
          });
        }
      } catch (err) {
        btn.textContent = 'Send Message'; btn.disabled = false;
      }
    });
  }

  /* ===========================================
     Init
     =========================================== */
  function init() {
    if (HAS_GSAP) {
      gsap.registerPlugin(ScrollTrigger);
    }

    initLenis();
    initHeader();
    initProgress();
    initTransitions();
    initMagnetic();
    initMobileMenu();
    initCartCount();
    initRails();

    captureRecentlyViewed();
    initRecentlyViewedSection();
    initRecommendations();

    initProductPage();
    initCollectionPage();
    initCartPage();
    initNewsletter();
    initContactForm();

    initHeroIntro();
    initReveals();
    initParallax();
    initMarquee();
    initEditorialPin();

    /* Layout settles after fonts/images — keep trigger positions honest.
       Also re-measure when a background tab becomes visible (Chrome
       freezes rAF for hidden tabs, so triggers may have gone stale). */
    window.addEventListener('load', function () {
      if (MOTION) ScrollTrigger.refresh();
    });
    document.addEventListener('visibilitychange', function () {
      if (MOTION && document.visibilityState === 'visible') {
        ScrollTrigger.refresh();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

